// IAMMUZIK AppContext — API-backed (Phase 2-4). No localStorage needed:
// the JWT lives in memory for the session, which works in the deployed
// preview's sandboxed iframe. Theme is client-side (default dark).
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Notification, Song, Theme, User } from '../types';
import { api, setToken, getToken } from '../lib/api';

export interface NewSongInput {
  file?: File;
  title: string;
  genre: string;
  secondaryGenres?: string[];
  mood?: string;
  bpm?: number;
  duration?: string;
  notes?: string;
  lyrics?: string;
  contentRating: 'CLEAN' | 'EXPLICIT';
  privacy: 'PRIVATE' | 'PUBLIC_REQUESTED';
  ownsRights: boolean;
  parentalPassword?: string;
  sourceJobId?: string;
  sourceType?: string;
}

interface AppContextValue {
  currentUser: User | null;
  isAuthed: boolean;
  loading: boolean;
  refreshUser: (u: User | null) => void;
  login: (username: string, password: string) => Promise<void>;
  register: (username: string, email: string, password: string, consent?: { ageGroup: string; termsAccepted: boolean; privacyAccepted: boolean; aiPolicyAccepted: boolean; contentRulesAccepted: boolean; parentalConsent?: boolean; parentalPassword?: string }) => Promise<void>;
  logout: () => void;

  songs: Song[];
  storageMb: number;
  limits: any;
  addSong: (input: NewSongInput) => Promise<Song>;
  updateSong: (id: string, patch: Partial<Song>) => Promise<void>;
  deleteSong: (id: string) => Promise<void>;
  requestWavePlacement: (id: string) => Promise<void>;
  approveSongForWave: (id: string) => Promise<void>;
  refreshSongs: () => Promise<void>;

  notifications: Notification[];
  unreadCount: number;
  markNotificationRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  refreshNotifications: () => Promise<void>;

  theme: Theme;
  toggleTheme: () => void;
}

const AppContext = createContext<AppContextValue | undefined>(undefined);



export function AppProvider({ children }: { children: ReactNode }) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [songs, setSongs] = useState<Song[]>([]);
  const [storageMb, setStorageMb] = useState(0);
  const [limits, setLimits] = useState<any>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState<Theme>('dark');

  // Restore session on mount — if a token exists in localStorage, call /api/auth/me
  useEffect(() => {
    const t = getToken();
    if (!t) { setLoading(false); return; }
    api.me()
      .then(({ user }) => {
        setCurrentUser(user);
        return Promise.all([loadSongs(), loadNotifications()]);
      })
      .then(() => setLoading(false))
      .catch(() => {
        // Token is invalid or expired — clear it
        setToken(null);
        setLoading(false);
      });
  }, []);

  // Presence heartbeat — send every 2 minutes while logged in
  useEffect(() => {
    if (!currentUser) return;
    api.heartbeat().catch(() => {});
    const interval = setInterval(() => {
      api.heartbeat().catch(() => {});
    }, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, [currentUser]);

  const loadSongs = useCallback(async () => {
    try {
      const data = await api.mySongs();
      setSongs(data.songs);
      setStorageMb(data.storageMb);
      setLimits(data.limits);
    } catch { /* ignore */ }
  }, []);

  const loadNotifications = useCallback(async () => {
    try {
      const data = await api.notifications();
      setNotifications(data.notifications);
    } catch { /* ignore */ }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([loadSongs(), loadNotifications()]);
    setLoading(false);
  }, [loadSongs, loadNotifications]);

  const login = useCallback(async (username: string, password: string) => {
    const { token, user } = await api.login(username, password);
    setToken(token);
    setCurrentUser(user);
    await loadAll();
  }, [loadAll]);

  const register = useCallback(async (username: string, email: string, password: string, consent?: { ageGroup: string; termsAccepted: boolean; privacyAccepted: boolean; aiPolicyAccepted: boolean; contentRulesAccepted: boolean; parentalConsent?: boolean; parentalPassword?: string }) => {
    const { token, user } = await api.register(username, email, password, consent);
    setToken(token);
    setCurrentUser(user);
    await loadAll();
  }, [loadAll]);

  const logout = useCallback(async () => {
    try { await api.logout(); } catch (e: any) { window.alert(e.message || 'Sign-out failed. Try again.'); return; }
    setToken(null);
    setCurrentUser(null);
    setSongs([]);
    setNotifications([]);
    setStorageMb(0);
    setLimits(null);
  }, []);

  const addSong = useCallback(async (input: NewSongInput): Promise<Song> => {
    let song: Song;
    if (input.file) {
      // File upload path
      const form = new FormData();
      form.append('audio', input.file);
      form.append('title', input.title);
      form.append('genre', input.genre);
      if (input.secondaryGenres?.length) form.append('secondaryGenres', JSON.stringify(input.secondaryGenres));
      if (input.mood) form.append('mood', input.mood);
      if (input.bpm) form.append('bpm', String(input.bpm));
      if (input.duration) form.append('duration', input.duration);
      if (input.notes) form.append('notes', input.notes);
      if (input.lyrics) form.append('lyrics', input.lyrics);
      form.append('contentRating', input.contentRating);
      form.append('privacy', input.privacy);
      form.append('ownsRights', String(input.ownsRights));
      if (input.parentalPassword) form.append('parentalPassword', input.parentalPassword);
      const result = await api.uploadSong(form);
      song = result.song;
    } else {
      // JSON save path — drafts or AI-generated content
      const result = await api.saveSong({
        title: input.title,
        genre: input.genre,
        secondaryGenres: input.secondaryGenres,
        mood: input.mood,
        bpm: input.bpm,
        duration: input.duration,
        notes: input.notes,
        lyrics: input.lyrics,
        contentRating: input.contentRating,
        privacy: input.privacy,
        ownsRights: input.ownsRights,
        sourceJobId: input.sourceJobId,
        sourceType: input.sourceType,
      });
      song = result.song;
    }
    await loadSongs();
    return song;
  }, [loadSongs]);

  const updateSong = useCallback(async (id: string, patch: Partial<Song>) => {
    const { song } = await api.patchSong(id, patch);
    setSongs((prev) => prev.map((s) => (s.id === id ? song : s)));
  }, []);

  const deleteSong = useCallback(async (id: string) => {
    await api.deleteSong(id);
    setSongs((prev) => prev.filter((s) => s.id !== id));
  }, []);

  const requestWavePlacement = useCallback(async (id: string) => {
    const { song } = await api.requestWave(id);
    setSongs((prev) => prev.map((s) => (s.id === id ? song : s)));
    await loadNotifications();
  }, [loadNotifications]);

  const approveSongForWave = useCallback(async (id: string) => {
    const { song } = await api.approveSong(id);
    setSongs((prev) => prev.map((s) => (s.id === id ? song : s)));
    await loadNotifications();
  }, [loadNotifications]);

  const markNotificationRead = useCallback(async (id: string) => {
    await api.readNotif(id);
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
  }, []);

  const markAllRead = useCallback(async () => {
    await api.readAll();
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme((t) => (t === 'dark' ? 'light' : 'dark'));
  }, []);

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.read).length,
    [notifications],
  );

  const value: AppContextValue = {
    currentUser, isAuthed: currentUser !== null, loading,
    login, register, logout,
    songs, storageMb, limits,
    addSong, updateSong, deleteSong, requestWavePlacement, approveSongForWave,
    refreshSongs: loadSongs,
    refreshUser: (u: User | null) => { if (u) setCurrentUser(u); },
    notifications, unreadCount, markNotificationRead, markAllRead, refreshNotifications: loadNotifications,
    theme, toggleTheme,
  };

  // expose api + demo password to the rest of the app
  (value as any).api = api;


  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useApp(): AppContextValue {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}
